import { useToast } from "@chakra-ui/react";

// Common toast configurations
const TOAST_CONFIG = {
  success: {
    duration: 5000,
    isClosable: true,
    status: "success",
  },
  error: {
    duration: 7000,
    isClosable: true,
    status: "error",
  },
  info: {
    duration: 3000,
    isClosable: true,
    status: "info",
  },
  warning: {
    duration: 5000,
    isClosable: true,
    status: "warning",
  },
};

// Custom hook for common toast functions
export const useCommonToast = () => {
  const toast = useToast();

  const showSuccess = (title, description) => {
    toast({
      title,
      description,
      ...TOAST_CONFIG.success,
    });
  };

  const showError = (title, description) => {
    toast({
      title,
      description,
      ...TOAST_CONFIG.error,
    });
  };

  const showInfo = (title, description) => {
    toast({
      title,
      description,
      ...TOAST_CONFIG.info,
    });
  };

  const showWarning = (title, description) => {
    toast({
      title,
      description,
      ...TOAST_CONFIG.warning,
    });
  };

  // Specific toast functions for common use cases
  const showItemCreated = () => {
    showSuccess("Item created.", "The item was successfully created.");
  };

  const showItemUpdated = () => {
    showSuccess("Item updated.", "The item was successfully updated.");
  };

  const showItemSoldOut = (isSoldOut) => {
    showSuccess(
      "Item updated.",
      `Item ${isSoldOut ? 'marked as sold out' : 'marked as in stock'}`
    );
  };

  const showMissingFields = (missingFields) => {
    const formattedMissingFields = missingFields
      .map((field) => `• ${field}`)
      .join("\n");

    showError(
      "Missing Required Fields",
      `Please fill out the following required fields:\n${formattedMissingFields}`,
      { whiteSpace: "pre-line" }
    );
  };

  const showImageUploadError = () => {
    showError("Image Upload Failed.", "Unable to upload the image.");
  };

  const showImageReplaceError = () => {
    showError("Image Replace Failed.", "Unable to replace the image.");
  };

  const showApiError = (error, defaultMessage = "An error occurred.") => {
    let errorMessage = error.response?.data?.message || 
                      error.response?.data || 
                      error.message || 
                      defaultMessage;

    // Handle specific error cases
    if (errorMessage.includes("value too long for type")) {
      errorMessage = "The image file name is too long.";
    }

    showError(defaultMessage, errorMessage);
  };

  const showOtpResent = () => {
    showInfo("OTP Resent", "A new OTP has been sent to your phone number.");
  };

  const showOtpVerified = () => {
    showSuccess("Verification Successful", "Your phone number and OTP have been verified.");
  };

  const showOtpError = () => {
    showError("Wrong OTP", "The OTP you entered is incorrect. Please try again.");
  };

  const showTooManyRequests = (maxRequests) => {
    showError(
      "Too Many Requests",
      `You have reached the maximum of ${maxRequests} requests. Please wait 5 minutes.`
    );
  };

  return {
    toast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showItemCreated,
    showItemUpdated,
    showItemSoldOut,
    showMissingFields,
    showImageUploadError,
    showImageReplaceError,
    showApiError,
    showOtpResent,
    showOtpVerified,
    showOtpError,
    showTooManyRequests,
  };
};

export default useCommonToast; 